/**
 * RulesPresentation - Full-screen presentation mode for church rules
 * Features: Slide-based display, auto/manual navigation, spiritual theme, weekly rota, announcements
 * Double-click to toggle fullscreen
 */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { QRCodeSVG } from 'qrcode.react';
import { DivineBackdrop, OrnateGoldBadge, GoldFooterPill } from '../components/LiveDisplay/shared';
import {
  ChevronLeft,
  ChevronRight,
  Play,
  Pause,
  Maximize,
  Minimize,
  BookOpen,
  Home,
  Calendar,
  User,
  Mic,
  Bell,
  AlertTriangle,
  PartyPopper,
  Heart,
  Image as ImageIcon,
  Smartphone,
  Copy,
  Check,
  X as XIcon,
  Sparkles
} from 'lucide-react';

const API = `${(process.env.REACT_APP_BACKEND_URL || window.location.origin)}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

// QR codes use the current page origin so they work on any domain (preview or production)
const getPublicUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http')) return url;
  return `${window.location.origin}${url}`;
};

const CopyBtn = ({ text, label, full }) => {
  const [copied, setCopied] = useState(false);
  return (
    <button
      onClick={() => { navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
      className={`${full ? 'w-full py-2 rounded-lg bg-purple-600/30 hover:bg-purple-600/50 text-purple-300 text-xs font-medium flex items-center justify-center gap-1.5' : 'p-1.5 rounded-lg hover:bg-white/10'} transition-all`}
    >
      {copied ? <Check className="w-3.5 h-3.5 text-green-400" /> : <Copy className="w-3.5 h-3.5 text-purple-400" />}
      {label && <span>{copied ? 'Copied!' : label}</span>}
    </button>
  );
};

// Announcement type styling
const ANNOUNCEMENT_STYLES = {
  general: { bg: 'from-blue-500 to-blue-600', icon: Bell, label: 'Announcement' },
  urgent: { bg: 'from-red-500 to-red-600', icon: AlertTriangle, label: 'Urgent Notice' },
  event: { bg: 'from-purple-500 to-purple-600', icon: Calendar, label: 'Upcoming Event' },
  prayer: { bg: 'from-amber-500 to-amber-600', icon: Heart, label: 'Prayer Request' },
  birthday: { bg: 'from-pink-500 to-pink-600', icon: PartyPopper, label: 'Celebration' },
  thanksgiving: { bg: 'from-green-500 to-green-600', icon: Heart, label: 'Thanksgiving' }
};

// Auto-fit text component for presentation slides
const SlideFitText = ({ text, maxSize = 48, minSize = 12, className = '', style = {} }) => {
  const textRef = React.useRef(null);
  const wrapRef = React.useRef(null);
  const [fontSize, setFontSize] = React.useState(minSize);

  React.useEffect(() => {
    const el = textRef.current;
    const wrap = wrapRef.current;
    if (!el || !wrap) return;

    const fit = () => {
      const wH = wrap.clientHeight;
      const wW = wrap.clientWidth;
      if (wH < 20 || wW < 20) return;
      let lo = minSize, hi = maxSize, best = minSize;
      while (lo <= hi) {
        const mid = Math.floor((lo + hi) / 2);
        el.style.fontSize = `${mid}px`;
        if (el.scrollHeight <= wH && el.scrollWidth <= wW) {
          best = mid;
          lo = mid + 1;
        } else {
          hi = mid - 1;
        }
      }
      setFontSize(best);
      el.style.fontSize = `${best}px`;
    };
    const t1 = setTimeout(fit, 50);
    const t2 = setTimeout(fit, 200);
    const t3 = setTimeout(fit, 500);
    let obs;
    if (typeof ResizeObserver !== 'undefined') {
      obs = new ResizeObserver(fit);
      obs.observe(wrap);
    }
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); obs?.disconnect(); };
  }, [text, maxSize, minSize]);

  return (
    <div ref={wrapRef} style={{ width: '100%', height: '100%', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div ref={textRef} className={className} style={{ ...style, fontSize: `${fontSize}px`, maxWidth: '100%', textAlign: 'center' }}>
        {text}
      </div>
    </div>
  );
};

// Random entrance-animation presets — one is picked per slide change so the
// presentation feels dynamic on screen without being distracting. Each preset
// supplies the initial / animate / exit / transition props for framer-motion.
const SLIDE_VARIANTS = [
  // Slide in from right
  { initial: { opacity: 0, x: 120 }, animate: { opacity: 1, x: 0 }, exit: { opacity: 0, x: -120 }, transition: { duration: 0.55, ease: 'easeInOut' } },
  // Slide in from left
  { initial: { opacity: 0, x: -120 }, animate: { opacity: 1, x: 0 }, exit: { opacity: 0, x: 120 }, transition: { duration: 0.55, ease: 'easeInOut' } },
  // Slide up from below
  { initial: { opacity: 0, y: 80 }, animate: { opacity: 1, y: 0 }, exit: { opacity: 0, y: -80 }, transition: { duration: 0.55, ease: 'easeOut' } },
  // Drop down from top
  { initial: { opacity: 0, y: -80 }, animate: { opacity: 1, y: 0 }, exit: { opacity: 0, y: 80 }, transition: { duration: 0.55, ease: 'easeOut' } },
  // Zoom in with slight blur
  { initial: { opacity: 0, scale: 0.85, filter: 'blur(8px)' }, animate: { opacity: 1, scale: 1, filter: 'blur(0px)' }, exit: { opacity: 0, scale: 1.08, filter: 'blur(6px)' }, transition: { duration: 0.55, ease: 'easeOut' } },
  // Zoom out (pop)
  { initial: { opacity: 0, scale: 1.15 }, animate: { opacity: 1, scale: 1 }, exit: { opacity: 0, scale: 0.9 }, transition: { duration: 0.5, ease: 'easeOut' } },
  // Rotate + fade
  { initial: { opacity: 0, rotate: -4, scale: 0.92 }, animate: { opacity: 1, rotate: 0, scale: 1 }, exit: { opacity: 0, rotate: 4, scale: 0.95 }, transition: { duration: 0.6, ease: 'easeInOut' } },
  // Horizontal flip
  { initial: { opacity: 0, rotateY: 60 }, animate: { opacity: 1, rotateY: 0 }, exit: { opacity: 0, rotateY: -60 }, transition: { duration: 0.6, ease: 'easeInOut' } },
  // Soft fade
  { initial: { opacity: 0 }, animate: { opacity: 1 }, exit: { opacity: 0 }, transition: { duration: 0.6, ease: 'easeInOut' } },
];

const pickVariant = () => SLIDE_VARIANTS[Math.floor(Math.random() * SLIDE_VARIANTS.length)];

// ─────────────────────────────────────────────────────────────────────────────
// WelcomeIntroSlide — shown as the first slide of the Rules & Tenets deck so
// the congregation sees the church identity card before the rules cycle.
// Mirrors the styling of the live `WelcomeDisplay` (gold ornate badge, big
// bold-halo serif title, gold divider, parish subtitle, gold footer pill).
// ─────────────────────────────────────────────────────────────────────────────
const WelcomeIntroSlide = ({ variant }) => {
  const v = variant || SLIDE_VARIANTS[8]; // default to soft fade
  return (
    <motion.div
      initial={v.initial}
      animate={v.animate}
      exit={v.exit}
      transition={v.transition}
      className="w-full max-w-7xl mx-auto text-center"
      style={{ height: '85vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}
      data-testid="rules-welcome-intro"
    >
      <OrnateGoldBadge>WELCOME TO</OrnateGoldBadge>

      <motion.h1
        initial={{ opacity: 0, scale: 0.94, filter: 'blur(14px)' }}
        animate={{ opacity: 1, scale: 1, filter: 'blur(0px)' }}
        transition={{ delay: 0.25, duration: 0.9, type: 'spring' }}
        className="text-white"
        style={{
          fontWeight: 900,
          fontSize: 'clamp(40px, 9vh, 132px)',
          lineHeight: 1.04,
          letterSpacing: '-0.02em',
          margin: '4vh 2vw 1.5vh',
          fontFamily: '"Cinzel", "Playfair Display", Georgia, serif',
          textShadow:
            '2px 2px 0 rgba(0,0,0,0.95), -2px 2px 0 rgba(0,0,0,0.95), 2px -2px 0 rgba(0,0,0,0.95), -2px -2px 0 rgba(0,0,0,0.95), 0 4px 16px rgba(0,0,0,0.9)',
        }}
      >
        CELESTIAL CHURCH OF CHRIST
      </motion.h1>

      <motion.div
        initial={{ scaleX: 0, opacity: 0 }}
        animate={{ scaleX: 1, opacity: 1 }}
        transition={{ delay: 0.6, duration: 0.55 }}
        style={{
          width: '320px', height: '2.5px', margin: '0.5vh 0 2.5vh',
          background: 'linear-gradient(90deg, transparent, #fde68a, transparent)',
          boxShadow: '0 0 14px rgba(251,191,36,0.7)',
        }}
      />

      <motion.h2
        initial={{ opacity: 0, y: -14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.75, duration: 0.5 }}
        className="text-white/95"
        style={{
          fontWeight: 800,
          fontSize: 'clamp(22px, 4.4vh, 60px)',
          letterSpacing: '0.01em',
          margin: 0,
          textShadow:
            '1.5px 1.5px 0 rgba(0,0,0,0.95), 0 3px 10px rgba(0,0,0,0.85), 0 1px 3px rgba(0,0,0,0.95)',
        }}
      >
        Valley of Berachah Parish · Dartford
      </motion.h2>

      <motion.div
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.0, duration: 0.6 }}
        style={{ marginTop: '5.5vh' }}
      >
        <GoldFooterPill>WE WELCOME YOU INTO THE PRESENCE OF THE LORD</GoldFooterPill>
      </motion.div>
    </motion.div>
  );
};

// Inspirational Message Slide — mirrors the bold styling of RuleSlide so the
// weekly message reads loud-and-clear on the church TVs. Title, message body
// and the underlying Bible verse all sit cleanly within one slide (no overlap).
const InspirationalSlide = ({ message, variant }) => {
  if (!message) return null;
  const hasVerse = !!(message.bible_verse || message.bible_reference);

  // Layout heights — keep total under 85vh to match the rest of the deck.
  const badgeH = '6vh';
  const titleH = '14vh';
  const verseH = hasVerse ? '14vh' : '0px';
  const bodyH = `calc(82vh - ${badgeH} - ${titleH} - ${verseH})`;

  const headlineShadow = '2px 2px 0 rgba(0,0,0,0.85), 0 4px 14px rgba(0,0,0,0.85), 0 1px 3px rgba(0,0,0,0.9)';
  const bodyShadow = '1.5px 1.5px 0 rgba(0,0,0,0.85), 0 3px 10px rgba(0,0,0,0.8), 0 1px 2px rgba(0,0,0,0.9)';

  const v = variant || SLIDE_VARIANTS[0];
  return (
    <motion.div
      initial={v.initial}
      animate={v.animate}
      exit={v.exit}
      transition={v.transition}
      className="w-full max-w-6xl mx-auto text-center"
      style={{ height: '85vh', display: 'flex', flexDirection: 'column', alignItems: 'center', overflow: 'hidden' }}
      data-testid="inspirational-slide"
    >
      {/* Section pill — same gold gradient as Rule slides */}
      <div style={{ height: badgeH, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <span
          className="px-7 py-2 rounded-full font-black uppercase tracking-wide text-slate-900 shadow-[0_8px_24px_rgba(251,191,36,0.45)] inline-flex items-center gap-2"
          style={{
            background: 'linear-gradient(180deg, #fde047 0%, #f59e0b 100%)',
            border: '2px solid rgba(255,255,255,0.35)',
            fontSize: 'clamp(13px, 1.9vh, 22px)',
            letterSpacing: '0.05em',
          }}
        >
          <Sparkles className="w-4 h-4" />
          Weekly Inspirational Message
        </span>
      </div>

      {/* Title */}
      <div style={{ height: titleH, flexShrink: 0, width: '100%', padding: '0.5vh 1vw' }}>
        <SlideFitText
          text={message.title || ''}
          maxSize={104}
          minSize={36}
          className="text-white leading-[1.05]"
          style={{
            fontWeight: 900,
            textShadow: headlineShadow,
            letterSpacing: '-0.01em',
            fontFamily: '"Inter", "Helvetica Neue", system-ui, sans-serif',
          }}
        />
        <div className="flex items-center justify-center gap-2 mt-1.5">
          <div className="h-[2px] w-12 bg-gradient-to-r from-transparent to-amber-400" />
          <div className="w-2.5 h-2.5 rotate-45 bg-amber-400 shadow-[0_0_12px_rgba(251,191,36,0.8)]" />
          <div className="h-[2px] w-12 bg-gradient-to-l from-transparent to-amber-400" />
        </div>
      </div>

      {/* Body — bold message, fills remaining space */}
      <div style={{ height: bodyH, width: '100%', padding: '0.5vh 2vw' }}>
        <SlideFitText
          text={message.message || ''}
          maxSize={70}
          minSize={20}
          className="text-white leading-[1.22] whitespace-pre-wrap"
          style={{
            fontWeight: 800,
            textShadow: bodyShadow,
            letterSpacing: '-0.005em',
          }}
        />
      </div>

      {/* Bible verse — premium glass card same style as RuleSlide */}
      {hasVerse && (
        <div style={{ height: verseH, flexShrink: 0, width: '100%', paddingTop: '0.5vh' }}>
          <div
            className="h-full flex flex-col items-center justify-center px-6 rounded-2xl"
            style={{
              background: 'linear-gradient(180deg, rgba(15,42,90,0.55) 0%, rgba(8,28,68,0.65) 100%)',
              border: '1.5px solid rgba(251,191,36,0.55)',
              boxShadow: '0 0 0 1px rgba(251,191,36,0.15), 0 12px 32px rgba(0,0,0,0.45)',
              backdropFilter: 'blur(8px)',
              overflow: 'hidden',
            }}
          >
            {message.bible_verse && (
              <p
                className="text-white italic leading-snug overflow-hidden"
                style={{
                  fontSize: 'clamp(14px, 2.1vh, 26px)',
                  display: '-webkit-box',
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: 'vertical',
                  textShadow: '0 1px 4px rgba(0,0,0,0.6)',
                  fontWeight: 700,
                  marginBottom: '4px',
                }}
              >
                &ldquo;{message.bible_verse}&rdquo;
              </p>
            )}
            {message.bible_reference && (
              <div className="flex items-center gap-3 shrink-0">
                <BookOpen className="w-5 h-5 text-amber-300 drop-shadow" />
                <span className="font-black tracking-wide text-amber-300" style={{ fontSize: 'clamp(15px, 2vh, 24px)', textShadow: '0 2px 6px rgba(0,0,0,0.5)' }}>
                  {message.bible_reference}
                </span>
              </div>
            )}
          </div>
        </div>
      )}
    </motion.div>
  );
};

// Simple RuleSlide Component without draggable
const RuleSlide = ({ rule, variant }) => {
  const hasImage = !!rule?.image_url;
  const hasBible = !!rule?.bible_passage;

  // Maximize content space — give body text most of the screen
  const badgeH = '8vh';
  const imageH = hasImage ? '11vh' : '0px';
  const bibleH = hasBible ? '10vh' : '0px';
  const titleH = '9vh';
  const contentH = `calc(82vh - ${badgeH} - ${imageH} - ${titleH} - ${bibleH})`;

  // Sharp drop-shadow tuned for TV legibility — tight blur values give crisp
  // edges instead of a soft halo that washes out the text.
  const headlineShadow = '2px 2px 0 rgba(0,0,0,0.85), 0 4px 14px rgba(0,0,0,0.85), 0 1px 3px rgba(0,0,0,0.9)';
  const bodyShadow = '1.5px 1.5px 0 rgba(0,0,0,0.85), 0 3px 10px rgba(0,0,0,0.8), 0 1px 2px rgba(0,0,0,0.9)';

  const v = variant || SLIDE_VARIANTS[0];
  return (
    <motion.div
      initial={v.initial}
      animate={v.animate}
      exit={v.exit}
      transition={v.transition}
      className="w-full max-w-6xl mx-auto text-center"
      style={{ height: '85vh', display: 'flex', flexDirection: 'column', alignItems: 'center', overflow: 'hidden' }}
    >
      {/* Section badge — shared HD ornate gold pill */}
      <div style={{ height: badgeH, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <OrnateGoldBadge>{(rule?.section || 'Church Tenets').toUpperCase()}</OrnateGoldBadge>
      </div>

      {/* Floating Bible / image — square card with golden ring + glow */}
      {hasImage && (
        <div style={{ height: imageH, flexShrink: 0, display: 'flex', justifyContent: 'center', padding: '0.4vh 0' }}>
          <div
            className="relative rounded-2xl overflow-hidden"
            style={{
              height: '100%',
              aspectRatio: '1',
              border: '3px solid rgba(251,191,36,0.85)',
              boxShadow: '0 0 0 4px rgba(251,191,36,0.2), 0 14px 40px rgba(251,191,36,0.4)',
            }}
          >
            <img src={rule.image_url} alt={rule.title} className="w-full h-full object-cover"
              onError={(e) => { e.target.style.display = 'none'; }} />
          </div>
        </div>
      )}

      {/* Display title — extra bold, chrome white, heavy drop-shadow */}
      <div style={{ height: titleH, flexShrink: 0, width: '100%', padding: '0.5vh 1vw' }}>
        <SlideFitText
          text={rule?.title || ''}
          maxSize={hasImage ? 92 : 110}
          minSize={36}
          className="text-white leading-[1.05]"
          style={{
            fontWeight: 900,
            textShadow: headlineShadow,
            letterSpacing: '-0.01em',
            fontFamily: '"Inter", "Helvetica Neue", system-ui, sans-serif',
          }}
        />
        {/* Decorative diamond divider under title */}
        <div className="flex items-center justify-center gap-2 mt-1.5">
          <div className="h-[2px] w-12 bg-gradient-to-r from-transparent to-amber-400" />
          <div className="w-2.5 h-2.5 rotate-45 bg-amber-400 shadow-[0_0_12px_rgba(251,191,36,0.8)]" />
          <div className="h-[2px] w-12 bg-gradient-to-l from-transparent to-amber-400" />
        </div>
      </div>

      {/* Body content — bold white, room to breathe */}
      <div style={{ height: contentH, width: '100%', padding: '0.5vh 1.5vw' }}>
        <SlideFitText
          text={rule?.content || ''}
          maxSize={hasImage ? 64 : 78}
          minSize={22}
          className="text-white leading-[1.18] whitespace-pre-wrap"
          style={{
            fontWeight: 800,
            textShadow: bodyShadow,
            letterSpacing: '-0.005em',
          }}
        />
      </div>

      {/* Bible Passage — premium glass card with gold accents */}
      {hasBible && (
        <div style={{ height: bibleH, flexShrink: 0, width: '100%', paddingTop: '0.5vh' }}>
          <div
            className="h-full flex flex-col items-center justify-center px-6 rounded-2xl"
            style={{
              background: 'linear-gradient(180deg, rgba(15,42,90,0.55) 0%, rgba(8,28,68,0.65) 100%)',
              border: '1.5px solid rgba(251,191,36,0.55)',
              boxShadow: '0 0 0 1px rgba(251,191,36,0.15), 0 12px 32px rgba(0,0,0,0.45)',
              backdropFilter: 'blur(8px)',
              overflow: 'hidden',
            }}
          >
            <div className="flex items-center gap-3 mb-1 shrink-0">
              <BookOpen className="w-5 h-5 text-amber-300 drop-shadow" />
              <span className="font-black tracking-wide text-amber-300" style={{ fontSize: 'clamp(15px, 2vh, 24px)', textShadow: '0 2px 6px rgba(0,0,0,0.5)' }}>
                {rule.bible_passage}
              </span>
            </div>
            {rule?.bible_text && (
              <p
                className="text-white/95 italic leading-snug overflow-hidden"
                style={{
                  fontSize: 'clamp(12px, 1.55vh, 18px)',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  textShadow: '0 1px 4px rgba(0,0,0,0.5)',
                  fontWeight: 500,
                }}
              >
                "{rule.bible_text}"
              </p>
            )}
          </div>
        </div>
      )}
    </motion.div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// QuoteSlide — auto-rotating Bible verse filler slide. Same altar background
// (rendered by parent) but a quote-centric typographic layout.
// ─────────────────────────────────────────────────────────────────────────────
const QuoteSlide = ({ quote, variant }) => {
  const headlineShadow = '2px 2px 0 rgba(0,0,0,0.85), 0 4px 14px rgba(0,0,0,0.85), 0 1px 3px rgba(0,0,0,0.9)';
  const refShadow = '1.5px 1.5px 0 rgba(0,0,0,0.85), 0 2px 6px rgba(0,0,0,0.85)';
  const v = variant || SLIDE_VARIANTS[0];

  return (
    <motion.div
      initial={v.initial}
      animate={v.animate}
      exit={v.exit}
      transition={v.transition}
      className="w-full max-w-5xl mx-auto text-center"
      style={{ height: '85vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', padding: '0 1.5vw' }}
    >
      {/* Category pill — same gold style as Rule pill for visual consistency */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '2.5vh' }}>
        <span
          className="px-7 py-2 rounded-full font-black uppercase tracking-wide text-slate-900 shadow-[0_8px_24px_rgba(251,191,36,0.45)]"
          style={{
            background: 'linear-gradient(180deg, #fde047 0%, #f59e0b 100%)',
            border: '2px solid rgba(255,255,255,0.35)',
            fontSize: 'clamp(13px, 1.9vh, 22px)',
            letterSpacing: '0.05em',
          }}
        >
          {quote?.category || 'Inspiration'}
        </span>
      </div>

      {/* Decorative open-quote glyph */}
      <div
        aria-hidden="true"
        className="text-amber-300/80"
        style={{
          fontFamily: 'Georgia, "Times New Roman", serif',
          fontSize: 'clamp(48px, 11vh, 132px)',
          lineHeight: '0.6',
          marginBottom: '1.2vh',
          textShadow: '0 0 28px rgba(251, 191, 36, 0.5)',
        }}
      >
        “
      </div>

      {/* The verse text — italic serif for scripture feel */}
      <div style={{ width: '100%', flex: '1 1 auto', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <SlideFitText
          text={quote?.text || ''}
          maxSize={84}
          minSize={26}
          className="text-white leading-[1.18]"
          style={{
            fontWeight: 700,
            fontStyle: 'italic',
            fontFamily: 'Georgia, "Times New Roman", serif',
            textShadow: headlineShadow,
            letterSpacing: '-0.005em',
            maxWidth: '95%',
          }}
        />
      </div>

      {/* Decorative close-quote glyph */}
      <div
        aria-hidden="true"
        className="text-amber-300/80"
        style={{
          fontFamily: 'Georgia, "Times New Roman", serif',
          fontSize: 'clamp(48px, 11vh, 132px)',
          lineHeight: '0.6',
          marginTop: '1.5vh',
          textShadow: '0 0 28px rgba(251, 191, 36, 0.5)',
        }}
      >
        ”
      </div>

      {/* Reference + diamond divider */}
      <div className="flex items-center justify-center gap-3 mt-3">
        <div className="h-[2px] w-16 bg-gradient-to-r from-transparent to-amber-400" />
        <div className="w-2.5 h-2.5 rotate-45 bg-amber-400 shadow-[0_0_12px_rgba(251,191,36,0.8)]" />
        <BookOpen className="w-5 h-5 text-amber-300 drop-shadow" />
        <p
          className="font-black text-amber-300 tracking-wide"
          style={{ fontSize: 'clamp(18px, 3vh, 36px)', textShadow: refShadow }}
        >
          {quote?.reference}
        </p>
        <div className="w-2.5 h-2.5 rotate-45 bg-amber-400 shadow-[0_0_12px_rgba(251,191,36,0.8)]" />
        <div className="h-[2px] w-16 bg-gradient-to-l from-transparent to-amber-400" />
      </div>
    </motion.div>
  );
};

const RulesPresentation = () => {
  const [rules, setRules] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [inspirational, setInspirational] = useState(null);
  const [weeklySchedule, setWeeklySchedule] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [stampKey, setStampKey] = useState(0); // triggers cinematic VOB stamp on slide change
  const [loading, setLoading] = useState(true);
  const [isAutoPlay, setIsAutoPlay] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [remoteCode, setRemoteCode] = useState('');
  const [showRemotePanel, setShowRemotePanel] = useState(false);
  const autoPlayInterval = 8000;
  const containerRef = useRef(null);
  const lastRemoteSlide = useRef(-1);

  const resolveImageUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    return `${BACKEND_URL}${url}`;
  };

  // Slide order: WELCOME intro → Rules → Bible Quotes → Inspirational → Announcements (with optional attached flyer) → Weekly Schedule
  // Note: standalone "Media Flyers" are intentionally excluded — flyers only
  // appear on stage when explicitly attached to an Active Announcement.
  const scheduleServices = weeklySchedule?.services || [];
  const inspirationalCount = inspirational ? 1 : 0;
  const WELCOME_SLIDE_COUNT = 1;
  const totalSlides =
    WELCOME_SLIDE_COUNT + rules.length + quotes.length +
    inspirationalCount + announcements.length + scheduleServices.length;

  const welcomeSlideIndex = 0;
  const ruleSlideStart = WELCOME_SLIDE_COUNT;
  const quoteSlideStart = ruleSlideStart + rules.length;
  const inspirationalSlideStart = quoteSlideStart + quotes.length;
  const announcementSlideStart = inspirationalSlideStart + inspirationalCount;
  const scheduleSlideStart = announcementSlideStart + announcements.length;

  const isWelcomeSlide = currentSlide === welcomeSlideIndex;
  const isRuleSlide = currentSlide >= ruleSlideStart && currentSlide < quoteSlideStart;
  const isQuoteSlide = currentSlide >= quoteSlideStart && currentSlide < inspirationalSlideStart;
  const isInspirationalSlide = currentSlide >= inspirationalSlideStart && currentSlide < announcementSlideStart;
  const isAnnouncementSlide = currentSlide >= announcementSlideStart && currentSlide < scheduleSlideStart;
  const isScheduleSlide = currentSlide >= scheduleSlideStart && currentSlide < scheduleSlideStart + scheduleServices.length;

  // Pick a random entrance animation each time the slide index changes so the
  // presentation feels alive on the church TVs — no two neighbours animate in
  // the same way.
  const slideVariant = useMemo(() => pickVariant(), [currentSlide]);
  const currentScheduleIndex = isScheduleSlide ? currentSlide - scheduleSlideStart : -1;
  const currentService = isScheduleSlide ? scheduleServices[currentScheduleIndex] : null;
  const currentQuote = isQuoteSlide ? quotes[currentSlide - quoteSlideStart] : null;

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [rulesRes, scheduleRes, announcementsRes, quotesRes, inspirationalRes] = await Promise.allSettled([
        axios.get(`${API}/rules/presentation`),
        axios.get(`${API}/rota/weekly-schedule`),
        axios.get(`${API}/special-announcements/active`),
        axios.get(`${API}/bible-quotes`),
        axios.get(`${API}/inspirational/latest`),
      ]);
      if (rulesRes.status === 'fulfilled') setRules(rulesRes.value.data.rules || []);
      if (scheduleRes.status === 'fulfilled') setWeeklySchedule(scheduleRes.value.data);
      if (announcementsRes.status === 'fulfilled') setAnnouncements(announcementsRes.value.data.announcements || []);
      if (quotesRes.status === 'fulfilled') setQuotes(quotesRes.value.data || []);
      if (inspirationalRes.status === 'fulfilled') setInspirational(inspirationalRes.value.data || null);
    } catch (error) {
      console.error('Error loading data:', error);
    }
    setLoading(false);
  };

  // Sync presentation state to backend whenever slide changes
  useEffect(() => {
    if (totalSlides === 0) return;
    const slideType = isWelcomeSlide ? 'welcome'
      : isRuleSlide ? 'rule'
      : isQuoteSlide ? 'quote'
      : isInspirationalSlide ? 'inspirational'
      : isAnnouncementSlide ? 'announcement'
      : 'schedule';
    const slides = [
      { type: 'welcome', title: 'Welcome to Celestial Church of Christ', content: 'Valley of Berachah Parish · Dartford', index: welcomeSlideIndex },
      ...rules.map((r, i) => ({ type: 'rule', title: r.title, content: r.content || '', index: ruleSlideStart + i })),
      ...quotes.map((q, i) => ({ type: 'quote', title: q.reference || '', content: q.text || '', bible_reference: q.reference || '', index: quoteSlideStart + i })),
      ...(inspirational ? [{ type: 'inspirational', title: inspirational.title || 'Weekly Inspirational Message', content: inspirational.message || '', bible_reference: inspirational.bible_reference || '', index: inspirationalSlideStart }] : []),
      ...announcements.map((a, i) => ({ type: 'announcement', title: a.title, content: a.content || '', announcement_type: a.type || 'general', has_flyer: !!a.flyer, flyer_url: a.flyer?.image_url || '', image_url: a.flyer?.image_url || '', index: announcementSlideStart + i })),
      ...scheduleServices.map((s, i) => ({
        type: 'schedule',
        title: s.service_display,
        date: s.date,
        service_type: s.service_type,
        assignments: (s.assignments || []).map(a => ({
          role_display: a.role_display,
          role: a.role,
          minister_name: a.minister_name || '',
          bible_passage: a.bible_passage || '',
        })),
        index: scheduleSlideStart + i,
      })),
    ];
    axios.post(`${API}/display/presentation/sync`, {
      current_slide: currentSlide,
      total_slides: totalSlides,
      slide_info: slideType,
      slides
    }).catch(() => {});
  }, [currentSlide, totalSlides, rules.length, announcements.length, quotes.length, inspirational]);

  // Trigger the cinematic VOB stamp overlay whenever the slide changes
  useEffect(() => {
    setStampKey(k => k + 1);
  }, [currentSlide]);

  // Poll for remote navigation commands
  useEffect(() => {
    if (!remoteCode) return;
    const poll = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/display/presentation/state`);
        const remoteSlide = res.data?.current_slide;
        if (remoteSlide !== undefined && remoteSlide !== lastRemoteSlide.current && remoteSlide !== currentSlide) {
          lastRemoteSlide.current = remoteSlide;
          setCurrentSlide(remoteSlide);
        }
      } catch (err) { console.debug('Remote poll error:', err.message); }
    }, 1000);
    return () => clearInterval(poll);
  }, [remoteCode, currentSlide]);

  const generateRemoteCode = async () => {
    try {
      const res = await axios.post(`${API}/display/presentation/create-session`);
      if (res.data?.code) setRemoteCode(res.data.code);
    } catch (e) { console.error('Error creating session:', e); }
  };

  // Auto-play functionality
  useEffect(() => {
    let interval;
    if (isAutoPlay && totalSlides > 0) {
      interval = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % totalSlides);
      }, autoPlayInterval);
    }
    return () => clearInterval(interval);
  }, [isAutoPlay, totalSlides, autoPlayInterval]);

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % totalSlides);
  }, [totalSlides]);

  const prevSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev - 1 + totalSlides) % totalSlides);
  }, [totalSlides]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      switch (e.key) {
        case 'ArrowRight':
        case ' ':
          e.preventDefault();
          nextSlide();
          break;
        case 'ArrowLeft':
          e.preventDefault();
          prevSlide();
          break;
        case 'Escape':
          if (isFullscreen) {
            exitFullscreen();
          }
          break;
        case 'f':
        case 'F':
          toggleFullscreen();
          break;
        case 'p':
        case 'P':
          setIsAutoPlay(!isAutoPlay);
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isFullscreen, isAutoPlay, totalSlides, nextSlide, prevSlide]);

  // Auto-hide controls
  useEffect(() => {
    let timeout;
    const handleMouseMove = () => {
      setShowControls(true);
      clearTimeout(timeout);
      timeout = setTimeout(() => {
        if (isFullscreen) {
          setShowControls(false);
        }
      }, 3000);
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      clearTimeout(timeout);
    };
  }, [isFullscreen]);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  const exitFullscreen = () => {
    if (document.fullscreenElement) {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  const goHome = () => {
    window.location.href = '/';
  };

  // Double-click to toggle fullscreen
  const handleDoubleClick = useCallback(() => {
    toggleFullscreen();
  }, []);

  // Format date for display
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { 
      weekday: 'short',
      day: 'numeric', 
      month: 'short' 
    });
  };

  // Get service color
  const getServiceColor = (serviceType) => {
    const colors = {
      'sunday': 'from-blue-500 to-blue-600',
      'wednesday': 'from-purple-500 to-purple-600',
      'friday': 'from-emerald-500 to-emerald-600',
      'first_thursday': 'from-amber-500 to-amber-600'
    };
    return colors[serviceType] || 'from-slate-500 to-slate-600';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-violet-950 via-purple-900 to-violet-950 flex items-center justify-center">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  if (rules.length === 0 && scheduleServices.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-violet-950 via-purple-900 to-violet-950 flex items-center justify-center">
        <div className="text-center">
          <BookOpen className="w-20 h-20 text-white/50 mx-auto mb-4" />
          <h2 className="text-2xl text-white mb-2">No Content Available</h2>
          <p className="text-white/60 mb-6">Add rules in the admin panel to display them here</p>
          <button
            onClick={goHome}
            className="px-6 py-3 bg-white/20 text-white rounded-xl hover:bg-white/30 transition-colors"
          >
            Go to Homepage
          </button>
        </div>
      </div>
    );
  }

  const currentRule = isRuleSlide ? rules[currentSlide - ruleSlideStart] : null;

  return (
    <div 
      className="h-screen relative overflow-hidden cursor-none"
      onDoubleClick={handleDoubleClick}
      style={{ backgroundColor: '#031026' }}
    >
      {/* Shared HD divine-light backdrop — same identity as Hymn / Bible slides */}
      <DivineBackdrop />

      {/* Soft vignette for extra TV depth */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse at center, transparent 65%, rgba(0,0,0,0.35) 100%)',
        }}
        aria-hidden="true"
      />

      {/* VOB Seal Watermark — mid-left, very faint (matches reference; new BG already provides identity) */}
      <div className="absolute pointer-events-none z-0" style={{ top: '50%', left: '4vw', transform: 'translateY(-50%)' }}>
        <img
          src="/church-logo.png?v=2"
          alt=""
          aria-hidden="true"
          className="object-contain"
          style={{
            width: 'min(28vh, 22vw)',
            height: 'min(28vh, 22vw)',
            opacity: 0.06,
            filter: 'drop-shadow(0 0 18px rgba(96, 165, 250, 0.3))',
          }}
          onError={(e) => { e.target.style.display = 'none'; }}
        />
      </div>

      {/* Cinematic VOB Stamp — quick gold sparkle in top-right corner on slide change (no longer covers content) */}
      <AnimatePresence>
        <motion.div
          key={`stamp-${stampKey}`}
          className="absolute pointer-events-none z-[60]"
          style={{ top: '12vh', right: '8vw' }}
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: [0, 0.7, 0], scale: [0.8, 1, 1.1] }}
          transition={{ duration: 0.9, times: [0, 0.45, 1], ease: 'easeOut' }}
        >
          <motion.img
            src="/church-logo.png?v=2"
            alt=""
            aria-hidden="true"
            className="object-contain"
            style={{
              width: 'min(14vh, 14vw)',
              height: 'min(14vh, 14vw)',
              filter: 'drop-shadow(0 0 22px rgba(251, 191, 36, 0.8)) drop-shadow(0 0 50px rgba(96, 165, 250, 0.4))',
            }}
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        </motion.div>
      </AnimatePresence>

      {/* Main Content */}
      <div className="relative z-10 h-screen flex flex-col items-center justify-center px-8 py-2 overflow-hidden">
        {/* Church Header with Logo — compact (hidden on welcome intro to avoid duplication) */}
        {!isWelcomeSlide && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
            style={{ flexShrink: 0, marginBottom: '0.5vh' }}
          >
          <div className="flex items-center justify-center gap-2 mb-0.5">
            <img 
              src="/church-logo.png?v=2" 
              alt="Church Logo" 
              className="object-contain"
              style={{ width: 'clamp(32px, 4vh, 48px)', height: 'clamp(32px, 4vh, 48px)' }}
              onError={(e) => { e.target.style.display = 'none'; }}
            />
            <div>
              <h1 className="font-bold text-amber-400 tracking-wide" style={{ fontSize: 'clamp(14px, 2vh, 24px)' }}>
                CELESTIAL CHURCH OF CHRIST
              </h1>
              <p className="text-white/80" style={{ fontSize: 'clamp(10px, 1.2vh, 14px)' }}>Valley of Berachah Parish</p>
            </div>
          </div>
          <div className="w-24 h-0.5 bg-gradient-to-r from-transparent via-amber-500 to-transparent mx-auto" />
        </motion.div>
        )}

        {/* Content Area */}
        <AnimatePresence mode="wait">
          {isWelcomeSlide ? (
            /* WELCOME INTRO — first slide of the deck */
            <WelcomeIntroSlide key="welcome-intro" variant={slideVariant} />
          ) : isRuleSlide ? (
            /* Rule Slide */
            <RuleSlide 
              key={`rule-${currentSlide}`}
              rule={currentRule}
              variant={slideVariant}
            />
          ) : isQuoteSlide && currentQuote ? (
            /* Bible Quote Slide — auto-rotating filler between rules and flyers */
            <QuoteSlide key={`quote-${currentSlide}`} quote={currentQuote} variant={slideVariant} />
          ) : isInspirationalSlide && inspirational ? (
            /* Inspirational Message Slide — AFTER QUOTES */
            <InspirationalSlide key={`inspirational-${currentSlide}`} message={inspirational} variant={slideVariant} />
          ) : isAnnouncementSlide ? (
            /* Announcement Slide — flyers, if attached, render in-line with the announcement */
            <motion.div
              key={`announcement-${currentSlide}`}
              initial={slideVariant.initial}
              animate={slideVariant.animate}
              exit={slideVariant.exit}
              transition={slideVariant.transition}
              className="w-full max-w-4xl mx-auto text-center"
              style={{ height: '72vh', display: 'flex', flexDirection: 'column', alignItems: 'center', overflow: 'hidden' }}
            >
              {(() => {
                const announcementIndex = currentSlide - announcementSlideStart;
                const announcement = announcements[announcementIndex];
                const style = ANNOUNCEMENT_STYLES[announcement?.type] || ANNOUNCEMENT_STYLES.general;
                const IconComponent = style.icon;
                const hasFlyer = !!announcement?.flyer;

                return (
                  <>
                    {/* Badge */}
                    <div style={{ flexShrink: 0, height: '6vh', display: 'flex', alignItems: 'center' }}>
                      <span className={`inline-flex items-center gap-2 px-5 py-2 bg-gradient-to-r ${style.bg} text-white rounded-full font-medium shadow-lg`}
                        style={{ fontSize: 'clamp(13px, 1.7vh, 18px)' }}>
                        <IconComponent className="w-5 h-5" />
                        {style.label}
                      </span>
                    </div>

                    {/* Title — auto-fit, EXTRA bold for TV legibility */}
                    <div style={{ flexShrink: 0, height: '14vh', width: '100%', padding: '0.5vh 1vw' }}>
                      <SlideFitText text={announcement?.title || ''} maxSize={88} minSize={28}
                        className="text-white leading-[1.05]"
                        style={{
                          fontWeight: 900,
                          textShadow: '2px 2px 0 rgba(0,0,0,0.85), 0 4px 14px rgba(0,0,0,0.85), 0 1px 3px rgba(0,0,0,0.9)',
                          letterSpacing: '-0.01em',
                        }} />
                    </div>

                    {/* Content + Optional Flyer — bold body + LARGE flyer so the congregation can actually see it */}
                    {hasFlyer ? (
                      <div style={{ flex: 1, minHeight: 0, width: '100%', display: 'flex', alignItems: 'stretch', gap: '2.5rem', justifyContent: 'center', textAlign: 'left' }}>
                        <div style={{ flex: '1 1 45%', minWidth: 0, display: 'flex', alignItems: 'center' }}>
                          <SlideFitText text={announcement?.content || ''} maxSize={52} minSize={18}
                            className="text-white whitespace-pre-wrap leading-[1.22]"
                            style={{
                              fontWeight: 800,
                              textShadow: '1.5px 1.5px 0 rgba(0,0,0,0.85), 0 3px 10px rgba(0,0,0,0.8), 0 1px 2px rgba(0,0,0,0.9)',
                            }} />
                        </div>
                        <div className="flex flex-col items-center gap-3 shrink-0" style={{ flex: '0 0 46vh' }}>
                          <img
                            src={resolveImageUrl(announcement.flyer.image_url)}
                            alt={announcement.flyer.title}
                            className="rounded-2xl shadow-2xl border border-white/15"
                            style={{
                              width: '46vh',
                              maxWidth: '100%',
                              maxHeight: '56vh',
                              objectFit: 'contain',
                              background: 'rgba(0,0,0,0.3)',
                            }}
                            data-testid="announcement-flyer-image"
                          />
                          <div className="bg-white p-2 rounded-xl shadow-lg">
                            <QRCodeSVG value={getPublicUrl(announcement.flyer.image_url)} size={96} level="M" bgColor="#ffffff" fgColor="#1e293b" />
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div style={{ flex: 1, minHeight: 0, width: '100%', padding: '0.5vh 1.5vw' }}>
                        <SlideFitText text={announcement?.content || ''} maxSize={66} minSize={22}
                          className="text-white whitespace-pre-wrap leading-[1.2]"
                          style={{
                            fontWeight: 800,
                            textShadow: '1.5px 1.5px 0 rgba(0,0,0,0.85), 0 3px 10px rgba(0,0,0,0.8), 0 1px 2px rgba(0,0,0,0.9)',
                            letterSpacing: '-0.005em',
                          }} />
                      </div>
                    )}
                  </>
                );
              })()}
            </motion.div>
          ) : isScheduleSlide && currentService ? (
            /* Weekly Schedule Slide — ONE service per slide, full width */
            <motion.div
              key={`schedule-${currentScheduleIndex}`}
              initial={slideVariant.initial}
              animate={slideVariant.animate}
              exit={slideVariant.exit}
              transition={slideVariant.transition}
              className="w-full max-w-7xl mx-auto"
            >
              {/* Schedule Header */}
              <div className="text-center mb-8">
                <span className="inline-flex items-center gap-2 px-6 py-2 bg-amber-500/20 text-amber-300 rounded-full text-sm font-medium border border-amber-500/30 mb-4">
                  <Calendar className="w-4 h-4" />
                  Weekly Service Schedule — {currentScheduleIndex + 1} of {scheduleServices.length}
                </span>
                <p className="text-white/60 text-lg md:text-xl font-medium">
                  {weeklySchedule?.week_label || 'This Week'} &bull; {weeklySchedule?.week_start} - {weeklySchedule?.week_end}
                </p>
              </div>

              {/* Single Service — Wider card for readability on the TV */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.15 }}
                className="bg-white/10 backdrop-blur-sm rounded-3xl overflow-hidden border border-white/20 w-full max-w-6xl mx-auto"
              >
                {/* Service Day Header — Large */}
                <div className={`px-10 py-8 bg-gradient-to-r ${getServiceColor(currentService.service_type)}`}>
                  <p className="text-white font-black text-4xl md:text-6xl tracking-tight" style={{ textShadow: '0 2px 12px rgba(0,0,0,0.45)' }}>{currentService.service_display}</p>
                  <p className="text-white/90 text-xl md:text-2xl mt-2 font-semibold">{formatDate(currentService.date)}</p>
                </div>
                
                {/* Assignments — Large readable text */}
                <div className="p-8 md:p-12 space-y-7">
                  {currentService.assignments?.map((assignment, aIdx) => (
                    <motion.div 
                      key={aIdx} 
                      className="flex items-start gap-6"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.2 + aIdx * 0.1 }}
                    >
                      <div className="p-4 bg-white/10 rounded-2xl shrink-0">
                        {assignment.role.includes('conductor') ? (
                          <User className="w-8 h-8 text-amber-400" />
                        ) : assignment.role.includes('preacher') ? (
                          <Mic className="w-8 h-8 text-emerald-400" />
                        ) : (
                          <BookOpen className="w-8 h-8 text-blue-400" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-white/60 text-base md:text-lg uppercase tracking-widest font-bold">
                          {assignment.role_display}
                        </p>
                        <p className="text-white font-black text-2xl md:text-4xl mt-1" style={{ textShadow: '0 2px 10px rgba(0,0,0,0.45)' }}>
                          {assignment.minister_name || <span className="text-white/30 italic font-semibold">Not assigned</span>}
                        </p>
                        {assignment.bible_passage && (
                          <p className="text-amber-300 text-xl md:text-2xl mt-2 font-bold">{assignment.bible_passage}</p>
                        )}
                      </div>
                    </motion.div>
                  ))}
                  {(!currentService.assignments || currentService.assignments.length === 0) && (
                    <p className="text-white/40 text-xl text-center py-6">No assignments yet</p>
                  )}
                </div>
              </motion.div>
            </motion.div>
          ) : null}
        </AnimatePresence>

        {/* Slide Indicator */}
        <div className="mt-10 flex items-center gap-2">
          {Array.from({ length: totalSlides }).map((_, index) => {
            const isWelcomeDot = index === welcomeSlideIndex;
            const isRule = index >= ruleSlideStart && index < quoteSlideStart;
            const isQuote = index >= quoteSlideStart && index < inspirationalSlideStart;
            const isInspirational = inspirational && index === inspirationalSlideStart;
            const isAnnouncement = index >= announcementSlideStart && index < scheduleSlideStart;

            const activeClass = isWelcomeDot ? 'bg-amber-200 w-8'
              : isQuote ? 'bg-violet-400 w-8'
              : isInspirational ? 'bg-sky-400 w-8'
              : isAnnouncement ? 'bg-red-500 w-8'
              : isRule ? 'bg-white w-8'
              : 'bg-amber-500 w-8';
            const idleClass = isWelcomeDot ? 'bg-amber-200/50 hover:bg-amber-200/70'
              : isQuote ? 'bg-violet-300/50 hover:bg-violet-300/70'
              : isInspirational ? 'bg-sky-300/50 hover:bg-sky-300/70'
              : isAnnouncement ? 'bg-red-400/50 hover:bg-red-400/70'
              : isRule ? 'bg-white/30 hover:bg-white/50'
              : 'bg-amber-400/50 hover:bg-amber-400/70';

            return (
              <button
                key={`slide-dot-${index}`}
                onClick={() => setCurrentSlide(index)}
                className={`w-3 h-3 rounded-full transition-all duration-300 ${index === currentSlide ? activeClass : idleClass}`}
                title={
                  isWelcomeDot
                    ? 'Welcome'
                    : isQuote
                      ? `Quote ${index - quoteSlideStart + 1}`
                      : isInspirational
                        ? 'Inspirational Message'
                        : isAnnouncement
                          ? `Announcement ${index - announcementSlideStart + 1}`
                          : isRule
                            ? `Rule ${index - ruleSlideStart + 1}`
                            : `Service ${index - scheduleSlideStart + 1}`
                }
              />
            );
          })}
        </div>
        
        <p className="text-white/50 text-sm mt-4">
          {currentSlide + 1} of {totalSlides}
          {isWelcomeSlide && ' • Welcome'}
          {isQuoteSlide && currentQuote && ` • ${currentQuote.reference}`}
          {isInspirationalSlide && ' • Inspirational'}
          {isAnnouncementSlide && ' • Announcement'}
          {isScheduleSlide && currentService && ` • ${currentService.service_display}`}
        </p>
      </div>

      {/* Controls Overlay */}
      <AnimatePresence>
        {showControls && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-20 pointer-events-none"
          >
            {/* Top Controls */}
            <div className="absolute top-4 right-4 flex items-center gap-2 pointer-events-auto">
              <button
                onClick={() => { if (!remoteCode) generateRemoteCode(); setShowRemotePanel(!showRemotePanel); }}
                className={`p-3 rounded-full text-white transition-colors ${showRemotePanel ? 'bg-purple-600' : 'bg-white/10 hover:bg-white/20'}`}
                title="Remote Control"
                data-testid="presentation-remote-btn"
              >
                <Smartphone className="w-5 h-5" />
              </button>
              <button
                onClick={goHome}
                className="p-3 bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors"
                title="Go Home"
              >
                <Home className="w-5 h-5" />
              </button>
              <button
                onClick={toggleFullscreen}
                className="p-3 bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors"
                title={isFullscreen ? 'Exit Fullscreen (Esc)' : 'Enter Fullscreen (F) or Double-click'}
              >
                {isFullscreen ? <Minimize className="w-5 h-5" /> : <Maximize className="w-5 h-5" />}
              </button>
            </div>

            {/* Remote Control Panel */}
            {showRemotePanel && remoteCode && (
              <div className="absolute top-16 right-4 pointer-events-auto bg-slate-900/95 backdrop-blur-xl rounded-2xl p-5 border border-purple-500/30 shadow-2xl w-72" data-testid="presentation-remote-panel">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Smartphone className="w-4 h-4 text-purple-400" />
                    <span className="text-purple-300 font-bold text-sm">Remote Control</span>
                  </div>
                  <button onClick={() => setShowRemotePanel(false)} className="text-white/40 hover:text-white">
                    <XIcon className="w-4 h-4" />
                  </button>
                </div>
                <div className="bg-white p-3 rounded-xl mb-3 flex justify-center">
                  <QRCodeSVG value={`${window.location.origin}/presentation-remote?code=${remoteCode}`} size={140} level="M" bgColor="#ffffff" fgColor="#6b21a8" />
                </div>
                <p className="text-white/50 text-xs text-center mb-2">Scan or enter code on your phone</p>
                <div className="flex items-center gap-2 bg-white/10 rounded-lg p-2">
                  <span className="text-xl font-mono font-bold text-purple-300 tracking-[0.2em] flex-1 text-center" data-testid="presentation-remote-code">{remoteCode}</span>
                  <CopyBtn text={remoteCode} />
                </div>
                <div className="mt-2">
                  <CopyBtn text={`${window.location.origin}/presentation-remote?code=${remoteCode}`} label="Copy Link" full />
                </div>
              </div>
            )}

            {/* Bottom Controls */}
            <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex items-center gap-4 pointer-events-auto">
              <button
                onClick={prevSlide}
                className="p-4 bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors"
                title="Previous (←)"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
              
              <button
                onClick={() => setIsAutoPlay(!isAutoPlay)}
                className={`p-4 rounded-full text-white transition-colors ${
                  isAutoPlay ? 'bg-amber-500 hover:bg-amber-600' : 'bg-white/10 hover:bg-white/20'
                }`}
                title={isAutoPlay ? 'Pause (P)' : 'Auto-play (P)'}
              >
                {isAutoPlay ? <Pause className="w-6 h-6" /> : <Play className="w-6 h-6" />}
              </button>
              
              <button
                onClick={nextSlide}
                className="p-4 bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors"
                title="Next (→ or Space)"
              >
                <ChevronRight className="w-6 h-6" />
              </button>
            </div>

            {/* Keyboard Hints */}
            <div className="absolute bottom-4 right-4 text-white/40 text-xs pointer-events-auto">
              <p>← → Navigate | Space Next | P Auto-play | F or Double-click Fullscreen | Esc Exit</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default RulesPresentation;
